import { useMutation, useQuery } from '@apollo/client'
import { SetHelpers } from '@dai/common'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material'
import { useEffect, useState } from 'react'

import {
  GET_PERMISSIONS,
  GET_TENANT_GROUPS,
  GetPermissionsQuery,
  GetPermissionsQueryVariables,
  GetTenantGroupsQuery,
  GetTenantGroupsQueryVariables,
  UPDATE_DAI_GROUP,
  UpdateDaiGroupMutation,
  UpdateDaiGroupMutationVariables,
} from '@/graphql'
import useT from '@/i18n/useT'

import { DAISelect } from '../components/DAISelect'
import { PermissionsSelectionList } from './PermissionsSelectionList'

type Group = GetTenantGroupsQuery['tenantGroups']['results'][0]

type Props = {
  group: Group | null
  setGroup: (group: Group | null) => void
}

export const EditGroupPermissionsDialog: React.FC<Props> = props => {
  const { group, setGroup } = props
  const { t } = useT()

  const [selectedPermissionIds, setSelectedPermissionIds] = useState<string[]>(
    [],
  )
  const [supervisingRoleId, setSupervisingRoleId] = useState('')
  const [updateError, setUpdateError] = useState<string | null>(null)

  useEffect(() => {
    if (group) {
      setSelectedPermissionIds(
        group.permissions.map(permission => permission.id),
      )
      setSupervisingRoleId(group.supervisingRole?.id ?? '')
      setUpdateError(null)
    }
  }, [group])

  const permissionsData = useQuery<
    GetPermissionsQuery,
    GetPermissionsQueryVariables
  >(GET_PERMISSIONS)

  const groupsData = useQuery<
    GetTenantGroupsQuery,
    GetTenantGroupsQueryVariables
  >(GET_TENANT_GROUPS)

  const [updateDaiGroup, { loading: updatingGroup }] = useMutation<
    UpdateDaiGroupMutation,
    UpdateDaiGroupMutationVariables
  >(UPDATE_DAI_GROUP)

  const togglePermission = (permissionId: string) => {
    setSelectedPermissionIds(
      selectedPermissionIds.includes(permissionId)
        ? selectedPermissionIds.filter(id => id !== permissionId)
        : [...selectedPermissionIds, permissionId],
    )
  }

  const permissions = permissionsData.data?.permissions || []
  const groups = (groupsData.data?.tenantGroups.results || []).filter(
    g => g.id !== group?.id,
  )

  return (
    <Dialog open={group !== null} onClose={() => setGroup(null)} fullWidth>
      <DialogTitle>
        {t('manageRoles.editDialog.title', 'Edit Role')}{' '}
        {group?.name ? `'${group.name}'` : ''}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} paddingTop={2}>
          {group?.isOwner ? (
            <Typography variant="caption">
              {t(
                'manageRoles.isOwnerDescription',
                'This is the top-level role',
              )}
            </Typography>
          ) : (
            <DAISelect
              title={t(
                'manageRoles.editDialog.supervisingRole',
                'Supervising Role',
              )}
              value={supervisingRoleId}
              handleChange={setSupervisingRoleId}
              options={[
                { value: '', label: '-' },
                ...groups.map(group => ({
                  value: group.id,
                  label: group.name,
                })),
              ]}
            />
          )}
          {permissionsData.loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : permissionsData.error ? (
            <Alert severity="error">{permissionsData.error.message}</Alert>
          ) : (
            <PermissionsSelectionList
              permissions={permissions}
              selectedPermissionIds={selectedPermissionIds}
              onTogglePermission={togglePermission}
            />
          )}
          {updateError && <Alert severity="error">{updateError}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setGroup(null)} variant="outlined">
          {t('common.cancel')}
        </Button>
        <Button
          type="submit"
          variant="contained"
          loading={updatingGroup}
          loadingPosition="end"
          disabled={
            SetHelpers.isSameSet(
              selectedPermissionIds,
              group?.permissions.map(permission => permission.id) || [],
            ) && supervisingRoleId === (group?.supervisingRole?.id ?? '')
          }
          onClick={async () => {
            if (group?.id) {
              setUpdateError(null)
              try {
                await updateDaiGroup({
                  variables: {
                    data: {
                      id: group.id,
                      permissionIds: selectedPermissionIds,
                      supervisingRoleId: supervisingRoleId || null,
                    },
                  },
                })
                setGroup(null)
              } catch (error) {
                setUpdateError(
                  error instanceof Error ? error.message : String(error),
                )
              }
            }
          }}
        >
          {t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
